// Site Hub screens. Pure: view in, HTML out.
// Words: COHESION_WORDS.md, Move 1. Mockup: MOCKUP_SITE_HUB.html rev 1 (approved 2026-10-23, 7:29 PM).

import { SITE_ZONES, clockIn, isSiteZone, siteZone, zoneFromState, zoneInfo } from "./site-time";
import { STEP_WORD, type DeviceCard, type HereRow, type HubStep, type LastStep, type SiteStage } from "./site-hub";
import { USED_FOR, type Contact, type Site } from "./sites";
import { esc } from "./training-screens";

// Program board (STEP_STATUS_WORDS.md rev 2 and 3, MOCKUP_STEP_STATUS.html, approved 2026-10-26).
// One green in three shades, light to dark: s0 Not Started, s1 In Process, s2 Complete. Gold marks the next step.
const SHADE = { gray: "s0", yellow: "s1", green: "s2" } as const;

/** A step's or a site's state: the shade and the word, a check on Complete. */
export function stepWord(l: HubStep["light"]): string {
  return `<span class="stx ${SHADE[l]}">${l === "green" ? "✓ " : ""}${STEP_WORD[l].toUpperCase()}</span>`;
}

/** What sets a step the admin does not sign off. */
const SET_BY: Record<string, string> = { "05": "Set by the study", "06": "Set by the labels", "08": "Set by Site Info" };

/** The first counted step that is not Complete: gold on the board. */
function nextStep(steps: readonly HubStep[]): string {
  return steps.find((x) => !x.off && x.light !== "green")?.n ?? "";
}

/** The board: the nine steps as one line of cells, the score above it. */
export function programBoard(steps: readonly HubStep[]): string {
  const counted = steps.filter((x) => !x.off);
  const done = counted.filter((x) => x.light === "green").length;
  const busy = counted.filter((x) => x.light === "yellow").length;
  const next = nextStep(steps);
  const cells = steps
    .map((x) => {
      const cls = x.off ? "off" : `${SHADE[x.light]}${x.n === next ? " next" : ""}`;
      const label = x.off ? "Coming" : x.name;
      const say = x.off ? "coming" : `${STEP_WORD[x.light]}${x.n === next ? ", next" : ""}`;
      return `<span class="pb-cell ${cls}" role="listitem" aria-label="${esc(`${x.n} ${x.name}, ${say}`)}"><span class="n">${esc(x.n)}</span><span class="nm">${esc(label)}</span></span>`;
    })
    .join("");
  return `<div class="pb">
    <div class="pb-head"><div class="pb-score"><span class="big">${done}<small>/${counted.length}</small></span><span class="of">complete</span>${busy ? `<span class="ip">${busy} in process</span>` : ""}</div>
      <div class="pb-key"><span><i class="s0"></i>Not Started</span><span><i class="s1"></i>In Process</span><span><i class="s2"></i>Complete</span><span><i class="gold"></i>Next</span></div></div>
    <div class="pb-line" role="list" aria-label="Program steps">${cells}</div>
  </div>`;
}

/**
 * One Program row: a lamp in the step's shade, the step, its status, and on 00 to 04 a button that says what it does:
 * Mark Complete, then Undo. The row opens the step. Steps the app sets say so.
 */
function stepRow(s: HubStep, busy: string): string {
  const red = s.red ? `<span class="hub-red">${s.red} overduty</span>` : "";
  const word = s.off ? "" : stepWord(s.light);
  const marked = s.markedLine ? `<small class="hub-by">${esc(s.markedLine)}</small>` : "";
  const shade = s.off ? "off" : SHADE[s.light];
  const inner = `<span class="lamp"></span><span class="num">${esc(s.n)}</span><span class="t"><b>${esc(s.name)}</b><small>${esc(s.fact)}</small>${marked}${red}</span>${word}`;
  const done = !!s.markedLine;
  const act = s.manual
    ? `<button class="hub-act" type="button" data-act="hub-mark" data-step="${esc(s.n)}" aria-pressed="${done}" aria-label="${done ? `Undo ${esc(s.name)} complete` : `Mark ${esc(s.name)} complete`}"${busy ? " disabled" : ""}>${done ? "Undo" : "Mark Complete"}</button>`
    : `<span class="hub-auto">${s.off ? "" : esc(SET_BY[s.n] ?? "")}</span>`;
  if (s.off || !s.href) return `<div class="hub-step ${shade}" aria-disabled="true"><div class="hub-go">${inner}</div>${act}</div>`;
  return `<div class="hub-step ${shade}"><a class="hub-go" href="${esc(s.href)}" data-hub-step="${esc(s.n)}">${inner}</a>${act}</div>`;
}

function hereRow(r: HereRow): string {
  const end = r.alert > 0 ? `<span class="hub-amb">${r.alert}</span>` : r.count !== null ? `<span class="hub-ct">${r.count}</span>` : "";
  return `<a class="hub-here" href="${esc(r.href)}"><span class="t"><b>${esc(r.name)}</b><small>${esc(r.line)}</small></span>${end}</a>`;
}

const pill = (on: boolean, yes: string, no: string) => `<span class="pill ${on ? "green" : "grey"}">${on ? yes : no}</span>`;

function row(k: string, v: string, e = ""): string {
  return `<div class="r"><div class="k">${esc(k)}</div><div class="v">${v}</div><div class="e">${e}</div></div>`;
}

function usDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

export interface HubView {
  site: Site & { driveFolder?: { id?: string; url?: string } | null };
  assetCount: number;
  steps: HubStep[];
  people: HereRow[];
  records: HereRow[];
  contacts: Contact[];
  device: DeviceCard;
  last: (LastStep & { when: string }) | null;
  /** The program read failed: the reason, with Try Again. */
  programError: string;
  flash: string;
  /** The step whose Done check is being saved. Blank when none. */
  markBusy?: string;
  /** Now, for the Time Zone row's "there now" clock. Passed in so the screen stays pure. */
  now?: Date;
}

/**
 * The Time Zone row (SITE_TIME_ZONE_WORDS.md, MOCKUP_SITE_TIME_ZONE.html panel A).
 * Saved: the zone and the clock there now. Not saved yet: where the zone came from, and Change to confirm it.
 */
export function zoneRowValue(s: { timeZone?: string | null; state?: string | null }, now: Date): string {
  const zone = siteZone(s);
  const z = zoneInfo(zone);
  const saved = isSiteZone(s.timeZone ?? "");
  const why = saved ? "" : zoneFromState(s.state ?? "") ? ` · from ${esc((s.state ?? "").toUpperCase())}, not confirmed` : " · not set yet";
  return `${esc(z.name)}<small class="tz-now">${esc(clockIn(zone, now))} there now${why}</small>`;
}

/** Change Time Zone (panel B): the zones with the clock in each, the site's one picked. */
export function zoneSheet(site: { name: string; timeZone?: string | null; state?: string | null }, picked: string, now: Date, busy: boolean, error = ""): string {
  const current = picked || siteZone(site);
  const opts = SITE_ZONES.map(
    (z) => `<button class="tz-opt${z.id === current ? " on" : ""}" type="button" data-act="site-zone-pick" data-zone="${esc(z.id)}"${busy ? " disabled" : ""}${z.id === current ? ' aria-pressed="true"' : ""}>
      <span><b>${esc(z.name)}</b><small>${esc(z.where)}</small></span><span class="tz-tm">${esc(clockIn(z.id, now))}</span></button>`,
  ).join("");
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="Time Zone">
    <div class="big">Time Zone · ${esc(site.name)}</div>
    <p>Every time and date at this site uses this zone: JSP start times, training dates, glove expiry, and when the night jobs run.</p>
    <div class="tz-opts">${opts}</div>
    <div class="note plain">Saved records keep their moment. Only how they read changes.</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="site-zone-save"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>
    <button class="go2" type="button" data-act="sheet-cancel"${busy ? " disabled" : ""}>Cancel</button>
  </div></div>`;
}

export function siteHub(v: HubView): string {
  const s = v.site;
  const place = [s.city, s.state].filter(Boolean).join(", ");
  const sub = `${place ? `${esc(place)} · ` : ""}${esc(String(v.assetCount))} ${v.assetCount === 1 ? "asset" : "assets"} · ${pill(s.isActive, "ACTIVE", "OFF")}`;
  const cont = v.last
    ? `<a class="hub-cont" href="${esc(v.last.href)}"><span>Continue: ${esc(v.last.name)}</span><small>${esc(v.last.when)}</small></a>`
    : "";
  const err = v.programError
    ? `<div class="note red">${esc(v.programError)} <button class="chg" type="button" data-act="hub-retry">Try Again</button></div>`
    : "";
  const contacts = v.contacts
    .map(
      (c) => `<a class="ct-row${c.isActive ? "" : " dim"}" href="#/sites/${esc(s.siteNumber)}/contacts/${esc(c.contactId)}">
        <span class="t"><b>${esc(c.name || c.email)}</b><small>${esc(c.role)}${c.email ? ` · ${esc(c.email)}` : ""}</small>
          <span class="tags">${USED_FOR.map((u) => `<span class="tg${c.usedFor?.[u.key] ? "" : " off"}">${esc(u.label)}</span>`).join("")}</span></span>
        ${pill(c.isActive, "ACTIVE", "OFF")}</a>`,
    )
    .join("");
  const folder = s.driveFolder?.url
    ? `<a class="hub-here" href="${esc(s.driveFolder.url)}" target="_blank" rel="noopener"><span class="t"><b>Drive Folder</b><small>${esc(s.siteNumber)} ${esc(s.name)} · 00 to 09</small></span>${pill(true, "SET", "")}</a>`
    : // This site's Asset ID Creator, where Build Folder sits (NAV_CRAWL_WORDS.md A9).
      `<a class="hub-here" href="#/assets/${esc(s.siteNumber)}"><span class="t"><b>Drive Folder</b><small>Build it from the Assessment Sheet</small></span>${pill(false, "", "NOT SET")}</a>`;
  return `<div class="ident"><div class="badge">${esc(s.siteNumber)}</div><div class="ident-text"><h1>${esc(s.name)}</h1><div class="sub">${sub}</div></div></div>
    ${v.flash}
    ${s.isActive ? "" : '<div class="note">Not active. Scans here show the guest page. Switches on at cutover.</div>'}
    ${cont}
    ${err}
    ${programBoard(v.steps)}
    <div class="hub-cols">
      <div class="hub-col">
        <h2 class="hub-sec">Program</h2>
        <div class="box">${v.steps.map((x) => stepRow(x, v.markBusy ?? "")).join("")}</div>
        <h2 class="hub-sec">People Here</h2>
        <div class="box">${v.people.map(hereRow).join("")}</div>
        <h2 class="hub-sec">Records Here</h2>
        <div class="box">${v.records.map(hereRow).join("")}</div>
      </div>
      <div class="hub-col">
        <h2 class="hub-sec">The Site</h2>
        <div class="box">
          <div class="boxhd"><div class="nm"><b>Details</b><small>Site number is locked. It starts every Asset ID here.</small></div>
            <a class="chg" href="#/sites/${esc(s.siteNumber)}/edit">Change</a></div>
          <div class="rows">
            ${row("Site No.", `<span class="mono">${esc(s.siteNumber)}</span>`, '<span class="lock">Locked</span>')}
            ${row("Name", esc(s.name))}
            ${row("City", esc(s.city) || '<span class="muted">Not set</span>')}
            ${row("State", esc(s.state) || '<span class="muted">Not set</span>')}
            ${row("Time Zone", zoneRowValue(s, v.now ?? new Date()), '<button class="chg" type="button" data-act="site-zone">Change</button>')}
            ${row("Arc Flash Assessed", s.arcFlashAssessedOn ? usDate(s.arcFlashAssessedOn) : '<span class="muted">Not set</span>')}
            ${row("Active", pill(s.isActive, "ACTIVE", "OFF"), `<button class="chg" type="button" data-act="site-active">${s.isActive ? "Turn Off" : "Turn On"}</button>`)}
          </div>
        </div>
        <div class="box">
          <div class="boxhd"><div class="nm"><b>Site Contacts</b><small>${v.contacts.filter((c) => c.isActive).length} active</small></div>
            <a class="chg" href="#/sites/${esc(s.siteNumber)}/contacts/new">Add Contact</a></div>
          ${contacts || '<div class="empty">No contacts yet. Add the plant manager and anyone who should get alerts or approve permits.</div>'}
        </div>
        <div class="box">
          <a class="hub-here" href="#/assets/${esc(s.siteNumber)}/outbox"><span class="t"><b>On This iPad</b><small>${esc(v.device.packLine)}</small></span>${pill(v.device.ready, "READY", "CHECK")}</a>
          ${folder}
          <a class="hub-here" href="#/sites/${esc(s.siteNumber)}/assets"><span class="t"><b>Asset List</b><small>Read only, with scan counts</small></span></a>
        </div>
      </div>
    </div>`;
}

/**
 * A site's stage and its ACTIVE pill, the right side of its Home row (COHESION_CHANGE_LOG.md change 3).
 * "3 of 8 done" then the stage on top, ACTIVE under the stage. One smooth bar, "Now:" under it.
 */
export function stageHtml(stage: SiteStage | null): string {
  // The board in miniature (STEP_STATUS_WORDS.md rev 3): one cell per counted step, in pairs so the gaps fall on
  // 25, 50, 75 and 100. Each cell carries its step's shade; gold outlines the next step.
  const blocks = stage?.blocks ?? "";
  const nextAt = blocks.split("").findIndex((c) => c !== "d");
  const shade = (c: string) => (c === "d" ? "s2" : c === "w" ? "s1" : "s0");
  const cells = blocks.split("").map((c, i) => `<i class="${shade(c)}${i === nextAt ? " next" : ""}"></i>`);
  const pairs: string[] = [];
  for (let i = 0; i < cells.length; i += 2) pairs.push(`<span class="pair">${cells.slice(i, i + 2).join("")}</span>`);
  const label = stage ? `${stage.done} of ${stage.total} steps complete` : "Stage not known on this device";
  const foot = stage
    ? `<span class="stx ${stage.kind === "dn" ? "s2" : stage.kind === "ip" ? "s1" : "s0"}">${stage.kind === "dn" ? "✓ " : ""}${esc(stage.word)}</span>${stage.now ? `<span class="nx">Next: ${esc(stage.now)}</span>` : ""}`
    : '<span class="stage-open">Open to see its stage</span>';
  const grid = stage ? `<span class="seg" role="img" aria-label="${label}">${pairs.join("")}</span>` : `<span class="seg empty" role="img" aria-label="${label}"></span>`;
  return `<span class="stage"><span class="pct" aria-hidden="true"><span>25%</span><span>50%</span><span>75%</span><span>100%</span></span>${grid}<span class="sfoot">${foot}</span></span>`;
}
